import type { ActionTemplateParameterResource, ControlType } from "@octopusdeploy/octopus-server-client";
import { bindParameterName, unbindParameterName, ControlType as ControlTypeEnum } from "@octopusdeploy/octopus-server-client";
import { useMemo } from "react";
import type * as React from "react";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import { controlTypeValueToTextMap } from "~/components/ControlType/ControlTypeSelector";
import { SelectWithActions } from "~/components/form/SelectWithActions/SelectWithActions";
interface ParameterOption {
    value: string;
    text: string;
    source: "Variable" | "Parameter";
}
interface BaseParameterSelectorProps {
    selectedParameter: string | null | undefined;
    onParameterChange(value: string | null): void;
    controlType: ControlType;
    allowClear: boolean;
    error?: string | undefined;
    label?: string;
    parameters: ActionTemplateParameterResource[] | undefined;
    bindValue?: boolean;
    actions?: React.ReactElement[];
    // Variables are currently only supported in project templates, this is likely to be refactored, probably out to a separate component in the near future
    variables?: Array<{
        Name: string;
        Type: string;
    }>;
}
export function BaseParameterSelector({ allowClear, controlType, error, label, onParameterChange, selectedParameter, parameters, bindValue = true, actions, variables }: BaseParameterSelectorProps) {
    const allOptions: ParameterOption[] = useMemo(() => {
        const parameterOptions: ParameterOption[] = parameters
            ?.filter((p) => p.DisplaySettings["Octopus.ControlType"] === controlType)
            ?.map((p: ActionTemplateParameterResource) => ({
            value: p.Name,
            text: p.Label || p.Name,
            source: "Parameter",
        })) || [];
        // Resource selection control types should not allow variables to be selected
        // These are special types that represent selections of specific resources (tags, feeds, environments, etc.)
        // rather than general string values
        const isUnsupportedResourceSelectionType = controlType === ControlTypeEnum.TargetTags ||
            controlType === ControlTypeEnum.Feed ||
            controlType === ControlTypeEnum.Environments ||
            controlType === ControlTypeEnum.TenantTags ||
            controlType === ControlTypeEnum.Teams ||
            controlType === ControlTypeEnum.Channels ||
            controlType === ControlTypeEnum.Project ||
            controlType === ControlTypeEnum.Package;
        if (!variables || variables.length === 0 || isUnsupportedResourceSelectionType) {
            return parameterOptions;
        }
        const expectedVariableType = getVariableTypeFromDisplaySettings(controlType);
        if (!expectedVariableType) {
            return parameterOptions;
        }
        const parameterNames = new Set(parameterOptions.map((p) => p.value));
        const variableOptions: ParameterOption[] = variables
            .filter((v) => v.Type === expectedVariableType && !parameterNames.has(v.Name))
            .map((v) => ({
            value: v.Name,
            text: v.Name,
            source: "Variable",
        }));
        return [...parameterOptions, ...variableOptions];
    }, [parameters, controlType, variables]);
    const unboundSelectedParameter = bindValue ? unbindParameterName(selectedParameter) : selectedParameter;
    const isSelectedParameterInvalid = unboundSelectedParameter ? allOptions.every((option) => option.value !== unboundSelectedParameter) : false;
    const missingParameterError = isSelectedParameterInvalid ? `Selected parameter '${unboundSelectedParameter}' can't be found` : undefined;
    const controlTypeText = controlTypeValueToTextMap.get(controlType);
    const displayLabel = label || `${controlTypeText} parameter`;
    const emptyMessage = allOptions?.length === 0 ? `No ${controlTypeText?.toLowerCase()} parameters found` : undefined;
    return (<SelectWithActions value={unboundSelectedParameter || undefined} onChange={(value) => {
            if (!value) {
                onParameterChange(null);
                return;
            }
            const selectedOption = allOptions.find((opt) => opt.value === value);
            // For Google Cloud accounts, don't bind if it's a Variable (regardless of bindValue setting)
            const shouldBind = controlType === ControlTypeEnum.GoogleCloudAccount && selectedOption?.source === "Variable" ? false : bindValue;
            const newValue = shouldBind ? bindParameterName(value) : value;
            onParameterChange(newValue);
        }} items={allOptions} label={displayLabel} empty={emptyMessage} allowClear={allowClear} allowFilter={true} error={error || missingParameterError} actions={actions || []}/>);
}
